import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useI18n } from '@/providers/i18n';
import { useMemo, useRef, useState } from 'react';
import type { PoseJointId, PoseJoints, PoseParams, PosePoint } from './types';
import { DEFAULT_POSE_PARAMS } from './types';

type PosePanelProps = {
  params: PoseParams;
  onChange: (patch: Partial<PoseParams>) => void;
};

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

const clampPoint = (p: PosePoint): PosePoint => ({ x: clamp01(p.x), y: clamp01(p.y) });

const PARENT: Record<PoseJointId, PoseJointId | null> = {
  head: 'neck',
  neck: 'hip',
  left_shoulder: 'neck',
  left_elbow: 'left_shoulder',
  left_wrist: 'left_elbow',
  right_shoulder: 'neck',
  right_elbow: 'right_shoulder',
  right_wrist: 'right_elbow',
  hip: null,
  left_hip: 'hip',
  left_knee: 'left_hip',
  left_ankle: 'left_knee',
  right_hip: 'hip',
  right_knee: 'right_hip',
  right_ankle: 'right_knee',
};

const CHILDREN: Record<PoseJointId, PoseJointId[]> = (() => {
  const next: Record<PoseJointId, PoseJointId[]> = {
    head: [],
    neck: [],
    left_shoulder: [],
    left_elbow: [],
    left_wrist: [],
    right_shoulder: [],
    right_elbow: [],
    right_wrist: [],
    hip: [],
    left_hip: [],
    left_knee: [],
    left_ankle: [],
    right_hip: [],
    right_knee: [],
    right_ankle: [],
  };
  for (const child of Object.keys(PARENT) as PoseJointId[]) {
    const parent = PARENT[child];
    if (parent) next[parent].push(child);
  }
  return next;
})();

const BONE_PAIRS: Array<[PoseJointId, PoseJointId]> = [
  ['head', 'neck'],
  ['neck', 'left_shoulder'],
  ['left_shoulder', 'left_elbow'],
  ['left_elbow', 'left_wrist'],
  ['neck', 'right_shoulder'],
  ['right_shoulder', 'right_elbow'],
  ['right_elbow', 'right_wrist'],
  ['neck', 'hip'],
  ['hip', 'left_hip'],
  ['left_hip', 'left_knee'],
  ['left_knee', 'left_ankle'],
  ['hip', 'right_hip'],
  ['right_hip', 'right_knee'],
  ['right_knee', 'right_ankle'],
];

const JOINT_ORDER: PoseJointId[] = [
  'head',
  'neck',
  'left_shoulder',
  'left_elbow',
  'left_wrist',
  'right_shoulder',
  'right_elbow',
  'right_wrist',
  'hip',
  'left_hip',
  'left_knee',
  'left_ankle',
  'right_hip',
  'right_knee',
  'right_ankle',
];

function toPx(p: PosePoint, w: number, h: number) {
  return { x: p.x * w, y: p.y * h };
}

function dist(a: PosePoint, b: PosePoint) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function computeBoneLengths(joints: PoseJoints): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [a, b] of BONE_PAIRS) {
    out[`${a}-${b}`] = dist(joints[a], joints[b]);
    out[`${b}-${a}`] = out[`${a}-${b}`];
  }
  return out;
}

function getDescendants(root: PoseJointId): PoseJointId[] {
  const out: PoseJointId[] = [];
  const stack = [...(CHILDREN[root] ?? [])];
  while (stack.length) {
    const cur = stack.pop();
    if (!cur) break;
    out.push(cur);
    for (const c of CHILDREN[cur] ?? []) stack.push(c);
  }
  return out;
}

function applyLockedDrag({
  joints,
  dragging,
  target,
  boneLengths,
}: {
  joints: PoseJoints;
  dragging: PoseJointId;
  target: PosePoint;
  boneLengths: Record<string, number>;
}): PoseJoints {
  const EPS = 1e-6;

  // Dragging the root translates the whole body without changing any lengths.
  if (dragging === 'hip') {
    const delta = { x: target.x - joints.hip.x, y: target.y - joints.hip.y };
    const next: PoseJoints = { ...joints };
    for (const jid of JOINT_ORDER) {
      next[jid] = clampPoint({ x: joints[jid].x + delta.x, y: joints[jid].y + delta.y });
    }
    return next;
  }

  const parent = PARENT[dragging];
  let nextPos = clampPoint(target);

  if (parent) {
    const parentPos = joints[parent];
    const L = boneLengths[`${parent}-${dragging}`];
    if (typeof L === 'number' && Number.isFinite(L) && L > 0) {
      let vx = nextPos.x - parentPos.x;
      let vy = nextPos.y - parentPos.y;
      let d = Math.hypot(vx, vy);
      if (d < EPS) {
        // fall back to previous direction to avoid NaNs
        vx = joints[dragging].x - parentPos.x;
        vy = joints[dragging].y - parentPos.y;
        d = Math.hypot(vx, vy);
      }
      if (d < EPS) {
        vx = 1;
        vy = 0;
        d = 1;
      }
      const s = L / d;
      nextPos = clampPoint({ x: parentPos.x + vx * s, y: parentPos.y + vy * s });
    }
  }

  const delta = { x: nextPos.x - joints[dragging].x, y: nextPos.y - joints[dragging].y };
  const idsToMove: PoseJointId[] = [dragging, ...getDescendants(dragging)];

  const next: PoseJoints = { ...joints };
  for (const jid of idsToMove) {
    next[jid] = clampPoint({ x: joints[jid].x + delta.x, y: joints[jid].y + delta.y });
  }
  return next;
}

function fromClientToNorm(e: PointerEvent | React.PointerEvent, svg: SVGSVGElement, w: number, h: number) {
  const rect = svg.getBoundingClientRect();
  const x = (('clientX' in e ? e.clientX : 0) - rect.left) / rect.width;
  const y = (('clientY' in e ? e.clientY : 0) - rect.top) / rect.height;
  return { x: clamp01(x), y: clamp01(y), w, h };
}

function jointRadius(jid: PoseJointId, isActive: boolean) {
  // Head should be the biggest joint.
  if (jid === 'head') return isActive ? 18 : 16;
  return isActive ? 9 : 7;
}

function jointHitRadius(jid: PoseJointId) {
  // Slightly larger hit area for head, consistent with visual size.
  return jid === 'head' ? 30 : 18;
}

export const PosePanel = ({ params, onChange }: PosePanelProps) => {
  const { t } = useI18n();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [dragging, setDragging] = useState<PoseJointId | null>(null);
  const dragBoneLengthsRef = useRef<Record<string, number> | null>(null);

  const vb = 280;
  const joints = params.joints;
  const lockLimbLength = params.lockLimbLength ?? true;

  const lines = useMemo(() => {
    return BONE_PAIRS.map(([a, b]) => {
      const pa = toPx(joints[a], vb, vb);
      const pb = toPx(joints[b], vb, vb);
      return { a, b, x1: pa.x, y1: pa.y, x2: pb.x, y2: pb.y };
    });
  }, [joints]);

  const applyDragTo = (jointId: PoseJointId, e: React.PointerEvent) => {
    const svg = svgRef.current;
    if (!svg) return;
    const pos = fromClientToNorm(e, svg, vb, vb);
    const target = { x: pos.x, y: pos.y };

    if (!lockLimbLength) {
      const nextJoints = { ...joints, [jointId]: target } as PoseJoints;
      onChange({ joints: nextJoints });
      return;
    }

    const boneLengths = dragBoneLengthsRef.current ?? computeBoneLengths(joints);
    dragBoneLengthsRef.current = boneLengths;
    const nextJoints = applyLockedDrag({ joints, dragging: jointId, target, boneLengths });
    onChange({ joints: nextJoints });
  };

  const handlePointerDown = (jointId: PoseJointId) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const svg = svgRef.current;
    if (!svg) return;
    try {
      svg.setPointerCapture(e.pointerId);
    } catch {}
    setDragging(jointId);
    dragBoneLengthsRef.current = lockLimbLength ? computeBoneLengths(joints) : null;
    applyDragTo(jointId, e);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragging) return;
    e.preventDefault();
    e.stopPropagation();
    applyDragTo(dragging, e);
  };

  const stopDragging = (e: React.PointerEvent) => {
    if (!dragging) return;
    const svg = svgRef.current;
    if (svg) {
      try {
        svg.releasePointerCapture(e.pointerId);
      } catch {}
    }
    setDragging(null);
    dragBoneLengthsRef.current = null;
  };

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-1">
          <div className="text-sm font-medium">{t('tool.pose.title')}</div>
          <div className="text-xs text-muted-foreground">({t('tool.pose.hint')})</div>
        </div>
        <div
          className="flex items-center gap-2"
          onClick={(e) => {
            // Prevent tool node selection / drag interactions.
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div className="flex items-center gap-2 rounded-md border bg-background/40 px-2 py-1">
            <div className="whitespace-nowrap text-xs text-muted-foreground">{t('tool.pose.lockLimbLength')}</div>
            <Switch checked={lockLimbLength} onCheckedChange={(v) => onChange({ lockLimbLength: !!v })} />
          </div>
          <Button
            size="sm"
            variant="secondary"
            className="h-7"
            onClick={() => {
              onChange({ joints: DEFAULT_POSE_PARAMS.joints });
            }}
          >
            {t('tool.pose.reset')}
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 rounded-lg border bg-background/50 p-2">
        <div className="aspect-square size-full">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${vb} ${vb}`}
            className="block size-full touch-none select-none"
            preserveAspectRatio="xMidYMid meet"
            onPointerMove={handlePointerMove}
            onPointerUp={stopDragging}
            onPointerCancel={stopDragging}
            onPointerLeave={stopDragging}
          >
            {/* background grid */}
            <defs>
              <pattern id="pose_grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
              </pattern>
            </defs>
            <rect x="0" y="0" width={vb} height={vb} fill="rgba(0,0,0,0.25)" rx="10" />
            <rect x="0" y="0" width={vb} height={vb} fill="url(#pose_grid)" rx="10" />

            {/* bones */}
            {lines.map((ln) => (
              <line
                key={`${ln.a}-${ln.b}`}
                x1={ln.x1}
                y1={ln.y1}
                x2={ln.x2}
                y2={ln.y2}
                stroke="rgba(255,255,255,0.75)"
                strokeWidth={4}
                strokeLinecap="round"
              />
            ))}

            {/* joints */}
            {JOINT_ORDER.map((jid) => {
              const p = toPx(joints[jid], vb, vb);
              const isActive = dragging === jid;
              return (
                <g key={jid}>
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={jointRadius(jid, isActive)}
                    fill={isActive ? 'rgba(125,254,241,0.95)' : 'rgba(11,170,254,0.95)'}
                    stroke="rgba(0,0,0,0.5)"
                    strokeWidth={2}
                    style={{ cursor: 'grab' }}
                    onPointerDown={handlePointerDown(jid)}
                  />
                  {/* hit area */}
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={jointHitRadius(jid)}
                    fill="transparent"
                    onPointerDown={handlePointerDown(jid)}
                  />
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    </div>
  );
};